import { useState } from 'react'
import { getConsentStatus, setConsentStatus } from './consent'
import { initializeAnalytics, optOutAnalytics } from './analytics'
import './ConsentBanner.css'

interface ConsentBannerProps {
  onConsentChange?: (accepted: boolean) => void
  onOpenPrivacyPolicy: () => void
}

export function ConsentBanner({ onConsentChange, onOpenPrivacyPolicy }: ConsentBannerProps) {
  // Only show the banner if consent is still pending. Read in a lazy initializer rather than an
  // effect: this is a client-only Vite app, so localStorage is available on the first render, and
  // seeding state directly avoids the extra render pass the effect used to cause.
  const [isVisible, setIsVisible] = useState(() => getConsentStatus().status === 'pending')

  const handleAccept = () => {
    setConsentStatus('accepted')
    setIsVisible(false)
    initializeAnalytics()
    onConsentChange?.(true)
  }

  const handleReject = () => {
    setConsentStatus('rejected')
    setIsVisible(false)
    optOutAnalytics()
    onConsentChange?.(false)
  }

  if (!isVisible) {
    return null
  }

  return (
    <div className="consent-banner" role="dialog" aria-labelledby="consent-title" aria-describedby="consent-description">
      <div className="consent-content">
        <h3 id="consent-title">Cookie Consent</h3>
        <p id="consent-description">
          We use Google Analytics to understand how visitors interact with our site.
          This helps us improve your experience. We respect your privacy and only collect
          anonymous usage data with your consent.
        </p>
        <p className="consent-note">
          You can change your preference at any time in Privacy Settings. Read our{' '}
          <button
            onClick={onOpenPrivacyPolicy}
            className="consent-link"
            type="button"
          >
            Privacy Policy
          </button>{' '}
          for more information.
        </p>
        <div className="consent-actions">
          <button
            onClick={handleAccept}
            className="consent-btn consent-btn-accept"
            aria-label="Accept analytics cookies"
          >
            Accept
          </button>
          <button
            onClick={handleReject}
            className="consent-btn consent-btn-reject"
            aria-label="Reject analytics cookies"
          >
            Reject
          </button>
        </div>
      </div>
    </div>
  )
}
